"use strict";
import { PLAYWRIGHT_CONNECT_ADDRESS } from "components/Web3Provider/constants";
import { injectedWithFallback } from "components/Web3Provider/injectedWithFallback";
import { WC_PARAMS } from "components/Web3Provider/walletConnect";
import { embeddedWallet } from "connection/EmbeddedWalletConnector";
import { UNISWAP_LOGO } from "ui/src/assets";
import { UNISWAP_WEB_URL } from "uniswap/src/constants/urls";
import { UNIVERSE_CHAINS_SORTED } from "uniswap/src/features/chains/chainInfo";
import { isTestnetChain } from "uniswap/src/features/chains/utils";
import { isPlaywrightEnv, isTestEnv } from "utilities/src/environment/env";
import { logger } from "utilities/src/logger/logger";
import { createClient } from "viem";
import { createConfig, fallback, http } from "wagmi";
import { coinbaseWallet, mock, safe, walletConnect } from "wagmi/connectors";
export const orderedTransportUrls = (chain) => {
  const orderedRpcUrls = [
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    ...chain.rpcUrls.interface?.http ?? [],
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    ...chain.rpcUrls.default?.http ?? [],
    ...chain.rpcUrls.public?.http ?? [],
    ...chain.rpcUrls.fallback?.http ?? []
  ];
  return Array.from(new Set(orderedRpcUrls.filter(Boolean)));
};
function createWagmiConnectors(params) {
  const { includeMockConnector } = params;
  const baseConnectors = [
    injectedWithFallback(),
    // There are no unit tests that expect WalletConnect to be included here,
    // so we can disable it to reduce log noise.
    ...isTestEnv() && !isPlaywrightEnv() ? [] : [walletConnect(WC_PARAMS)],
    embeddedWallet(),
    coinbaseWallet({
      appName: "Uniswap",
      // CB SDK doesn't pass the parent origin context to their passkey site
      // Flagged to CB team and can remove UNISWAP_WEB_URL once fixed
      appLogoUrl: `${UNISWAP_WEB_URL}${UNISWAP_LOGO}`,
      reloadOnDisconnect: false
    }),
    safe()
  ];
  return includeMockConnector ? [
    ...baseConnectors,
    mock({
      features: {},
      accounts: [PLAYWRIGHT_CONNECT_ADDRESS]
    })
  ] : baseConnectors;
}
const CHAINS = [UNIVERSE_CHAINS_SORTED[0], ...UNIVERSE_CHAINS_SORTED];
function createWagmiConfig(params) {
  const { connectors, onFetchResponse = defaultOnFetchResponse } = params;
  return createConfig({
    chains: CHAINS,
    connectors,
    client({ chain }) {
      return createClient({
        chain,
        batch: { multicall: true },
        pollingInterval: 12e3,
        transport: fallback(
          orderedTransportUrls(chain).map(
            (url) => http(url, { onFetchResponse: (response) => onFetchResponse(response, chain, url) })
          )
        )
      });
    }
  });
}
const defaultOnFetchResponse = (response, chain, url) => {
  if (response.status !== 200) {
    const message = `RPC provider returned non-200 status: ${response.status}`;
    if (isTestnetChain(chain.id)) {
      logger.warn("wagmiConfig.ts", "client", message, {
        extra: {
          chainId: chain.id,
          url
        }
      });
    } else {
      logger.error(new Error(message), {
        extra: {
          chainId: chain.id,
          url
        },
        tags: {
          file: "wagmiConfig.ts",
          function: "client"
        }
      });
    }
  }
};
const defaultConnectors = createWagmiConnectors({
  includeMockConnector: isPlaywrightEnv()
});
export const wagmiConfig = createWagmiConfig({ connectors: defaultConnectors });
